import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { useAppSelector, useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import { selectModal, useCohortEditable } from '@verantos-dev/app-state-cohort-builder';
import { selectResearchAssistantIsWaitingForResponse } from '@verantos-dev/app-state-research-assistant';
import {
  getCohortBuilderPath,
  getCohortsPath,
  getProjectsBasePath,
} from '@verantos-dev/core-constants';
import { CohortBuilderPageTab } from '@verantos-dev/core-enums';
import { useCohortBuilderData } from '@verantos-dev/data-access-cohorts';
import { type PageHeaderAction } from '@verantos-dev/shared-components';
import { CohortDefinitionStatus } from '@verantos-dev/types-cohorts';

import { useValidateCohortDefinition } from './useValidateCohortDefinition';
import {
  CohortBuilderActionDialogType,
  useCohortBuilderModalContext,
} from '../../../../../contexts/CohortBuilderModalContext';
import { useCheckCohortSummaryForErrors } from '../../../../../hooks/common/useCheckCohortSummaryForErrors';

export const useCohortBuilderPageActions = (
  activePageTab: CohortBuilderPageTab
): PageHeaderAction[] => {
  const navigate = useNavigate();
  const intl = useIntl();
  const isWaitingForRAResponse = useAppSelector(selectResearchAssistantIsWaitingForResponse);
  const restrictSaveProcess = useFeatureFlagSplit('restrict_save_process_on_error', false);
  // state  for determining whether save and process buttons are enabled
  const { data, projectId, cohortId, cohortVersionId } = useCohortBuilderData();
  const {
    isCohortDefinitionValid,
    hasDslValue,
    hasUnsavedChanges,
    hasNonExistingConcepts,
    isEventNameMappingValid,
  } = useValidateCohortDefinition();
  // State for determining if the page is ready for submit, drives whether actions are returned
  const { isEditable, isCohortProfileLoading, isCohortVersionLoading } = useCohortEditable();
  const isReadyForSubmit = !isCohortProfileLoading && !isCohortVersionLoading && !!data?.data;

  // Modal states
  const { openDialog, activeDialog } = useCohortBuilderModalContext();
  const modal = useAppSelector(selectModal);

  // Research Assistant state
  const isResearchAssistantEnabled = activePageTab === CohortBuilderPageTab.researchAssistant;

  const isProcessing = data?.data.latestStatus === CohortDefinitionStatus.PROCESSING;
  const isProcessed = data?.data.latestStatus === CohortDefinitionStatus.PROCESSED;
  const isFinal = data?.data.latestStatus === CohortDefinitionStatus.FINAL;
  const isOwnerAndReadyForSubmit = isEditable && isReadyForSubmit && !isFinal;
  const {
    eventIdsWithMissingEventNames,
    eventIdsWithDuplicateEventNames,
    simpleEventIdsWithNonExistingConcepts,
    exclusionCriteriaIdsWithMissingEventNames,
    exclusionCriteriaIdsWithDuplicateNames,
    inclusionCriteriaIdsWithMissingEventNames,
    inclusionCriteriaIdsWithDuplicateNames,
  } = useCheckCohortSummaryForErrors();

  const cohortBuilderPath = projectId
    ? cohortId && cohortVersionId
      ? `${getCohortBuilderPath(projectId, cohortId, cohortVersionId)}?pageTab=${
          isResearchAssistantEnabled
            ? CohortBuilderPageTab.events
            : CohortBuilderPageTab.researchAssistant
        }` // route to cohort builder if cohort id
      : getCohortsPath(projectId) // route to cohorts list if no cohort id
    : getProjectsBasePath(); // route to projects list if no project id)

  const hasConceptOrEventError =
    simpleEventIdsWithNonExistingConcepts.length > 0 ||
    eventIdsWithMissingEventNames.length > 0 ||
    eventIdsWithDuplicateEventNames.length > 0 ||
    exclusionCriteriaIdsWithMissingEventNames.length > 0 ||
    exclusionCriteriaIdsWithDuplicateNames.length > 0 ||
    inclusionCriteriaIdsWithMissingEventNames.length > 0 ||
    inclusionCriteriaIdsWithDuplicateNames.length > 0;

  const baseButtonDisabledLogic =
    modal !== undefined || // edit modals are not open
    activeDialog !== null || // another action menu is not open
    !hasDslValue || // must have a DSL value
    !isOwnerAndReadyForSubmit || // must be owner and ready for submit
    isProcessing || // must not be processing
    isWaitingForRAResponse || // must not be waiting for RA response
    !isEventNameMappingValid || // must have valid event name mapping
    (restrictSaveProcess && hasNonExistingConcepts) || // must not have non-existing concepts
    (restrictSaveProcess && hasConceptOrEventError); // must not have concept or event errors

  const saveButtonDisabledLogic = baseButtonDisabledLogic || !hasUnsavedChanges;
  const processButtonDisabledLogic = baseButtonDisabledLogic || !isCohortDefinitionValid;

  const saveButtonTooltip =
    restrictSaveProcess && saveButtonDisabledLogic
      ? intl.formatMessage({
          id: 'projects.cohortBuilderPage.header.saveButtonDisabledTooltip',
        })
      : undefined;
  const processButtonTooltip =
    restrictSaveProcess && processButtonDisabledLogic
      ? intl.formatMessage({
          id: 'projects.cohortBuilderPage.header.processButtonDisabledTooltip',
        })
      : undefined;
  const saveAndProcessButtonTooltip =
    restrictSaveProcess && baseButtonDisabledLogic
      ? intl.formatMessage({
          id: 'projects.cohortBuilderPage.header.saveAndProcessButtonDisabledTooltip',
        })
      : undefined;

  return [
    {
      id: 'viewModeAction',
      onClickHandler: () => {
        navigate(cohortBuilderPath);
      },
      label: isResearchAssistantEnabled
        ? intl.formatMessage({
            id: 'projects.cohortBuilderPage.header.hideResearchAssistantButton',
          })
        : intl.formatMessage({
            id: 'projects.cohortBuilderPage.header.showResearchAssistantButton',
          }),
      btnType: 'secondary',
    },
    {
      id: 'pageHeaderActionSave',
      label: intl.formatMessage({ id: 'projects.cohortBuilderPage.header.saveButton' }),
      onClickHandler: async () => {
        if (projectId && cohortId && hasDslValue) {
          // only call if active study and cohort
          openDialog(CohortBuilderActionDialogType.SAVE_COHORT_DEFINITION);
        }
      },
      btnType: 'secondary',
      tooltipText: saveAndProcessButtonTooltip || saveButtonTooltip,
      disabled: saveButtonDisabledLogic,
    },
    {
      id: 'pageHeaderActionProcess',
      label: intl.formatMessage({ id: 'projects.cohortBuilderPage.header.processButton' }),
      onClickHandler: async () => {
        // only call if active study and cohort
        if (isCohortDefinitionValid) {
          openDialog(CohortBuilderActionDialogType.PROCESS_COHORT_DEFINITION);
        }
      },
      btnType: 'primary',
      tooltipText: saveAndProcessButtonTooltip || processButtonTooltip,
      disabled: processButtonDisabledLogic,
    },
  ];
};
